import { Button } from '@components/Button'
import { useGetForumTopics } from '@services/api/graphql/comments'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator } from 'react-native'
import { useToastController } from '@tamagui/toast'
import { Text, XStack, YStack, isWeb } from 'tamagui'
import { CommentEmptyState } from './CommentEmptyState'
import { CommentModal } from './CommentModal'
import { TopicForm } from './TopicForm'
import { TopicItem } from './TopicItem'

type ToastType = 'success' | 'error'

interface ICommentThreadProps {
  courseContentId: string
}

export function CommentThread({ courseContentId }: ICommentThreadProps) {
  const { t } = useTranslation('Comments')
  const toast = useToastController()
  const [showForm, setShowForm] = useState(false)

  const handleToast = useCallback(
    (type: ToastType, message: string) => {
      toast?.show?.(message, { type })
    },
    [toast],
  )

  const {
    topics,
    totalCount,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    error,
    refetch,
  } = useGetForumTopics({ courseContentId })

  const isEmpty = !loading && topics.length === 0
  const title = useMemo(() => {
    if (totalCount === 0) return t('titleEmpty')
    if (totalCount === 1) return t('titleCount_one', { count: totalCount })
    return t('titleCount_other', { count: totalCount })
  }, [totalCount, t])

  return (
    <YStack
      gap={totalCount > 0 ? '$spacing-inset-md' : '$spacing-inset-xs'}
      p="$spacing-inset-xs"
      testID="comment-thread"
    >
      <XStack alignItems="center">
        <Text
          fontFamily="$mulishBold"
          fontSize="$font-size-xs"
          color="$accent"
          fontWeight="bold"
        >
          {title}
        </Text>
      </XStack>

      {/* Web: composer inline sempre visível (sem modal/botão full-width). */}
      {isWeb && (
        <TopicForm
          courseContentId={courseContentId}
          onCreated={() => refetch()}
          onToast={handleToast}
        />
      )}

      {!isWeb && (
        <CommentModal
          open={showForm}
          onClose={() => setShowForm(false)}
          courseContentId={courseContentId}
          onCreated={() => refetch()}
          onToast={handleToast}
        />
      )}

      {loading && topics.length === 0 && (
        <YStack alignItems="center" py="$spacing-inset-lg">
          <ActivityIndicator size="large" />
        </YStack>
      )}

      {error && (
        <YStack
          alignItems="center"
          gap="$spacing-inset-xs"
          py="$spacing-inset-lg"
          testID="comment-thread-error"
        >
          <Text
            fontFamily="$mulishRegular"
            fontSize="$font-size-xxs"
            color="$danger-300"
            textAlign="center"
          >
            {t('loadError')}
          </Text>
          <Button
            variant="secondary"
            size="small"
            onPress={() => refetch()}
            testID="comment-thread-retry"
          >
            {t('retry')}
          </Button>
        </YStack>
      )}

      {/* No web o composer inline acima já é a "caixa" para comentar; o
          empty-state (com CTA que abre modal) fica só no nativo. */}
      {isEmpty && !error && !isWeb && (
        <CommentEmptyState onPress={() => setShowForm(true)} />
      )}

      {!isWeb && totalCount > 0 && (
        <Button
          variant="primary"
          onPress={() => setShowForm(true)}
          testID="comment-thread-new-topic"
        >
          {t('newTopic.cta')}
        </Button>
      )}

      <YStack gap="$spacing-inset-sm">
        {topics.map((topic) => (
          <TopicItem key={topic.id} topic={topic} onToast={handleToast} />
        ))}
      </YStack>

      {hasMore && (
        <Button
          variant="secondary"
          size="small"
          onPress={() => loadMore()}
          disabled={loadingMore}
          loading={loadingMore}
          testID="comment-thread-load-more"
        >
          {t('loadMore')}
        </Button>
      )}
    </YStack>
  )
}
